import { cn } from '../lib/utils';

export type PersonRole = 'Intermediary' | 'Buyer' | 'Seller';

export interface PersonDisplayProps {
  /** Display name, when known. */
  name?: string | null;
  /** Email — line 2 under a name, or line 1 when there is no name. */
  email?: string | null;
  /** Firm/organisation — shown instead of the email on line 2 when given. */
  org?: string | null;
  /** Role tag, only where several roles mix (Access, Activity). */
  role?: PersonRole;
  /** md: 32px avatar, two lines. sm: 24px avatar, two lines. inline: 24px avatar
   * + one line (dense table cells; the email moves to the tooltip). */
  size?: 'md' | 'sm' | 'inline';
  /** Shown when there is neither a name nor an email (e.g. a system event). */
  fallback?: string;
  className?: string;
}

/** Up to two initials: from the name's words, else the email's local part. */
export function personInitials(name?: string | null, email?: string | null): string {
  const fromName = (name ?? '').trim();
  if (fromName) {
    const words = fromName.split(/\s+/).filter(Boolean);
    const first = words[0]?.[0] ?? '';
    const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
  }
  const local = (email ?? '').split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length > 1) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

/**
 * How a person appears anywhere in the apps (DataRoom UI "PersonDisplay"): an
 * initials avatar, the name, and the email (or firm) beneath. With no name the
 * email is the one line. Long values truncate with an ellipsis — an email never
 * pushes a table wider — and the full value is in the tooltip.
 */
export function PersonDisplay({
  name,
  email,
  org,
  role,
  size = 'md',
  fallback = 'Unknown',
  className,
}: PersonDisplayProps) {
  const trimmedName = name?.trim() || null;
  const primary = trimmedName ?? email ?? fallback;
  const secondary = trimmedName ? (org ?? email ?? null) : (org ?? null);
  const initials = personInitials(trimmedName, email) || '?';
  const title = [trimmedName, email, org].filter(Boolean).join(' · ') || undefined;
  const avatar = (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-pill bg-primary-soft font-bold tracking-wide text-primary-ink',
        size === 'md' ? 'h-8 w-8 text-xs' : 'h-6 w-6 text-[10px]',
      )}
    >
      {initials}
    </span>
  );
  const roleTag = role ? (
    <span className="ml-1.5 rounded-sm bg-surface-2 px-1.5 py-px text-[11px] font-semibold leading-4 text-ink-2">
      {role}
    </span>
  ) : null;

  if (size === 'inline') {
    return (
      <span className={cn('inline-flex min-w-0 max-w-full items-center gap-2', className)} title={title}>
        {avatar}
        <span className="min-w-0 truncate font-medium">{primary}</span>
        {roleTag}
      </span>
    );
  }
  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-2.5', className)} title={title}>
      {avatar}
      <span className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center">
          <span className="truncate font-semibold">{primary}</span>
          {roleTag}
        </span>
        {secondary ? <span className="truncate text-xs text-ink-3">{secondary}</span> : null}
      </span>
    </span>
  );
}
